/* yl-card-gallery.js — listing-card image gallery (snippets/yl-species-card.liquid).
 *
 * Markup contract (only rendered when a card has more than one image):
 *   [data-yl-card-gallery]          card root (<article class="yl-species-card">)
 *     [data-yl-card-track]          horizontal scroll-snap row inside the product link
 *       [data-yl-card-slide]        one per image; slides 2+ carry img[data-yl-src]/[data-yl-srcset]
 *     [data-yl-card-nav=prev|next]  <button>s outside the link, state via aria-disabled
 *     [data-yl-card-dot]            aria-hidden indicator dots
 *
 * Everything is delegated from document because cards arrive later (infinite scroll,
 * Section Rendering swaps), so there is no per-card init. Images after the first ship
 * without src; they are hydrated on first intent (pointerover / focusin / touchstart)
 * or when the track scrolls, one slide ahead of the visible one.
 */
(function () {
  'use strict';

  var ROOT = '[data-yl-card-gallery]';
  var TRACK = '[data-yl-card-track]';
  var SLIDE = '[data-yl-card-slide]';
  var NAV = '[data-yl-card-nav]';
  var DOT = '[data-yl-card-dot]';
  var READY_CLASS = 'yl-card-gallery-ready';

  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var queued = typeof WeakSet === 'function' ? new WeakSet() : null;

  // CSS shows the arrows only once this file has run, so no-JS visitors never get dead buttons.
  document.documentElement.classList.add(READY_CLASS);

  function closestRoot(node) {
    if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') return null;
    return node.closest(ROOT);
  }

  function getTrack(root) {
    return root ? root.querySelector(TRACK) : null;
  }

  function getSlides(track) {
    return track ? track.querySelectorAll(SLIDE) : [];
  }

  function hydrateSlide(slide) {
    if (!slide) return;
    var img = slide.querySelector('img[data-yl-src]');
    if (!img) return;
    var src = img.getAttribute('data-yl-src');
    var srcset = img.getAttribute('data-yl-srcset');
    img.removeAttribute('data-yl-src');
    img.removeAttribute('data-yl-srcset');
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    // Re-join the shared broken-image fallback in yololife-ui-controls.js only once a real src exists.
    img.classList.remove('yl-species-card__img-pending');
    img.classList.add('yl-species-card__img');
  }

  function currentIndex(track, count) {
    var width = track.clientWidth;
    if (!width) return 0;
    var index = Math.round(Math.abs(track.scrollLeft) / width);
    if (index < 0) return 0;
    if (index > count - 1) return count - 1;
    return index;
  }

  function hydrateAround(slides, index) {
    hydrateSlide(slides[index]);
    hydrateSlide(slides[index + 1]);
  }

  function sync(root) {
    var track = getTrack(root);
    if (!track) return;
    var slides = getSlides(track);
    var count = slides.length;
    if (count < 2) return;
    var index = currentIndex(track, count);

    hydrateAround(slides, index);

    var dots = root.querySelectorAll(DOT);
    for (var i = 0; i < dots.length; i += 1) {
      dots[i].classList.toggle('is-active', i === index);
    }

    var navs = root.querySelectorAll(NAV);
    for (var j = 0; j < navs.length; j += 1) {
      var atEdge = navs[j].getAttribute('data-yl-card-nav') === 'prev' ? index <= 0 : index >= count - 1;
      navs[j].setAttribute('aria-disabled', atEdge ? 'true' : 'false');
    }
  }

  function queueSync(root) {
    if (!root) return;
    if (queued) {
      if (queued.has(root)) return;
      queued.add(root);
    }
    window.requestAnimationFrame(function () {
      if (queued) queued.delete(root);
      sync(root);
    });
  }

  function prime(root) {
    if (!root || root.hasAttribute('data-yl-card-primed')) return;
    root.setAttribute('data-yl-card-primed', '');
    var track = getTrack(root);
    var slides = getSlides(track);
    if (slides.length < 2) return;
    hydrateAround(slides, currentIndex(track, slides.length));
  }

  function step(button) {
    var root = closestRoot(button);
    var track = getTrack(root);
    if (!track) return;
    if (button.getAttribute('aria-disabled') === 'true') return;
    var slides = getSlides(track);
    var count = slides.length;
    if (count < 2) return;

    var direction = button.getAttribute('data-yl-card-nav') === 'prev' ? -1 : 1;
    var target = currentIndex(track, count) + direction;
    if (target < 0 || target > count - 1) return;

    hydrateAround(slides, target);
    // One step = one track width; the slide's own offset avoids drift when the width is fractional.
    var slide = slides[target];
    var left = slide.offsetParent === track ? slide.offsetLeft : target * track.clientWidth;
    var behavior = reducedMotion && reducedMotion.matches ? 'auto' : 'smooth';
    try {
      track.scrollTo({ left: left, behavior: behavior });
    } catch (error) {
      track.scrollLeft = left;
    }
    queueSync(root);
  }

  document.addEventListener('pointerover', function (event) {
    prime(closestRoot(event.target));
  }, { passive: true });

  document.addEventListener('focusin', function (event) {
    prime(closestRoot(event.target));
  });

  document.addEventListener('touchstart', function (event) {
    prime(closestRoot(event.target));
  }, { passive: true });

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var button = target.closest(NAV);
    if (!button || !closestRoot(button)) return;
    step(button);
  });

  // Element scroll events do not bubble, but they do reach a capturing listener on document.
  document.addEventListener('scroll', function (event) {
    var target = event.target;
    if (!target || target.nodeType !== 1 || typeof target.matches !== 'function' || !target.matches(TRACK)) return;
    queueSync(closestRoot(target));
  }, { capture: true, passive: true });
})();
